import { useEffect, useRef } from 'react';

type BBox = { minLat: number; minLon: number; maxLat: number; maxLon: number; name?: string };
type Circle = { lat: number; lon: number; radiusKm: number; name?: string };
type Polygon = { points: Array<{ lat: number; lon: number }>; name?: string };

let leafletLoading: Promise<any> | null = null;
async function ensureLeaflet(): Promise<any> {
  if ((window as any).L) return (window as any).L;
  if (!leafletLoading) {
    leafletLoading = new Promise((resolve, reject) => {
      try {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(css);
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.async = true;
        s.onload = () => resolve((window as any).L);
        s.onerror = (e) => reject(e);
        document.body.appendChild(s);
      } catch (e) { reject(e); }
    });
  }
  return leafletLoading;
}

export default function GeoMapModal({
  open,
  onClose,
  bboxes = [],
  circles = [],
  polygons = [],
  bgBboxes = [],
  bgCircles = [],
  points = [],
  title = 'Access Policy Map',
}: {
  open: boolean;
  onClose: () => void;
  bboxes?: BBox[];
  circles?: Circle[];
  polygons?: Polygon[];
  bgBboxes?: BBox[];
  bgCircles?: Circle[];
  points?: Array<{ lat: number; lon: number; label?: string; tooltipHtml?: string }>;
  title?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let map: any;
    let teardown = () => {};
    (async () => {
      if (!open) return;
      const L = await ensureLeaflet();
      if (!ref.current) return;
      const node = ref.current;
      // Initialize map
      map = L.map(node, { center: [0, 0], zoom: 2, zoomControl: true, attributionControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      const layers: any[] = [];
      const boundsList: any[] = [];
      // Draw background policy shapes first (muted styles)
      (bgBboxes || []).forEach((b) => {
        if (![b.minLat,b.minLon,b.maxLat,b.maxLon].every((v:any)=>Number.isFinite(Number(v)))) return;
        const rect = L.rectangle([[Number(b.minLat), Number(b.minLon)], [Number(b.maxLat), Number(b.maxLon)]], { color: '#64748b', weight: 1, fillOpacity: 0.06 });
        rect.addTo(map);
        if (b.name) rect.bindTooltip(`Policy: ${b.name}`);
        layers.push(rect);
        boundsList.push(L.latLngBounds([Number(b.minLat), Number(b.minLon)], [Number(b.maxLat), Number(b.maxLon)]));
      });
      (bgCircles || []).forEach((c) => {
        if (![c.lat,c.lon,c.radiusKm].every((v:any)=>Number.isFinite(Number(v)))) return;
        const r = Math.max(1, Number(c.radiusKm || 0)) * 1000;
        const circle = L.circle([Number(c.lat), Number(c.lon)], { radius: r, color: '#64748b', weight: 1, fillOpacity: 0.06 });
        circle.addTo(map);
        if (c.name) circle.bindTooltip(`Policy: ${c.name}`);
        layers.push(circle);
        try { boundsList.push(circle.getBounds()); } catch {}
      });
      // Draw bboxes
      (bboxes || []).forEach((b) => {
        if (![b.minLat,b.minLon,b.maxLat,b.maxLon].every((v:any)=>Number.isFinite(Number(v)))) return;
        const rect = L.rectangle([[Number(b.minLat), Number(b.minLon)], [Number(b.maxLat), Number(b.maxLon)]], { color: '#3b82f6', weight: 2, fillOpacity: 0.12 });
        rect.addTo(map);
        if (b.name) rect.bindTooltip(b.name);
        layers.push(rect);
        boundsList.push(L.latLngBounds([Number(b.minLat), Number(b.minLon)], [Number(b.maxLat), Number(b.maxLon)]));
      });
      // Draw circles
      (circles || []).forEach((c) => {
        if (![c.lat,c.lon,c.radiusKm].every((v:any)=>Number.isFinite(Number(v)))) return;
        const r = Math.max(1, Number(c.radiusKm || 0)) * 1000;
        const circle = L.circle([Number(c.lat), Number(c.lon)], { radius: r, color: '#22c55e', weight: 2, fillOpacity: 0.12 });
        circle.addTo(map);
        if (c.name) circle.bindTooltip(c.name);
        layers.push(circle);
        try { boundsList.push(circle.getBounds()); } catch {}
      });

      // Draw polygons
      (polygons || []).forEach((p) => {
        const pts = Array.isArray(p?.points) ? p.points.filter(pt => Number.isFinite(Number(pt?.lat)) && Number.isFinite(Number(pt?.lon))).map(pt => [Number(pt.lat), Number(pt.lon)]) : [];
        if (pts.length >= 3) {
          const poly = L.polygon(pts as any, { color: '#a855f7', weight: 2, fillOpacity: 0.12 });
          poly.addTo(map);
          if (p.name) poly.bindTooltip(p.name);
          layers.push(poly);
          try { boundsList.push(poly.getBounds()); } catch {}
        }
      });

      // Draw points (locations)
      (points || []).forEach((p) => {
        if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lon)) return;
        const m = L.circleMarker([p.lat, p.lon], { radius: 6, color: '#f59e0b', weight: 2, fillColor: '#f59e0b', fillOpacity: 0.9 });
        m.addTo(map);
        const html = p.tooltipHtml || p.label || '';
        if (html) m.bindTooltip(html, { sticky: true, direction: 'top', opacity: 0.9 });
        layers.push(m);
        try { boundsList.push(L.latLngBounds([p.lat, p.lon], [p.lat, p.lon])); } catch {}
      });

      try {
        const fit = () => {
          if (boundsList.length > 0) {
            let total = boundsList[0];
            for (let i = 1; i < boundsList.length; i++) total = total.extend(boundsList[i]);
            map.invalidateSize();
            map.fitBounds(total.pad(0.12));
          } else {
            map.setView([0, 0], 2);
          }
        };
        // Slight defer to ensure modal layout settles before fit
        setTimeout(fit, 0);
      } catch {}

      teardown = () => {
        try { map && map.remove(); } catch {}
      };
    })();
    return () => { teardown(); };
  }, [open, JSON.stringify(bboxes), JSON.stringify(circles), JSON.stringify(polygons), JSON.stringify(bgBboxes), JSON.stringify(bgCircles)]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ width: '92vw', maxWidth: 980, padding: 12 }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn secondary" onClick={onClose}>Close</button>
        </div>
        <div ref={ref} style={{ width: '100%', height: '70vh', borderRadius: 8, overflow: 'hidden' }} />
      </div>
    </div>
  );
}
